<template>
  <c-input-container
    v-bind="$attrs"
    ref-cy="search-box"
    :class="classes"
  >
    <c-icon icon="search-loupe" class="icon" size="20" />

    <input
      v-bind="computedAttrs"

      ref="input"
      ref-cy="search-box-input"

      :value="value"
      :disabled="disabled"
      :class="[ 'input', { '--is-focused': focused } ]"
      :style="{ '--height': `${$options.height}px`, display: 'initial' }"

      v-on="computedListeners"

      @blur="onBlur"
      @keyup.esc="onEsc"
      @focus="focused = true"
      @input="$emit('input', $event.target.value)"
    />

    <c-button
      v-if="value && !disabled"
      grey
      icon="delete-disabled"
      class="cancel -default"
      type="button"
      :size="20"
      :icon-size="12"
      @click.stop="resetValue"
    />

    <c-button
      v-if="value && !disabled"
      flat
      icon="delete-disabled"
      class="cancel -theme-convenia-green"
      type="button"
      :size="20"
      :icon-size="12"
      @click.stop="resetValue"
    />
  </c-input-container>
</template>

<script>
import { defineAsyncComponent } from 'vue'
import useVue3Migration from '@convenia/composables/useVue3Migration'
import CInputContainer from '@convenia/components/CInputContainer'
import CIcon from '@convenia/components/CIcon'
import props from './props'

/**
 * Search Field component
 */
const height = 40

export default {
  name: 'CSearchBox',

  components: {
    CInputContainer,
    CIcon,
    CButton: defineAsyncComponent(() => import('@convenia/components/CButton/index.vue'))
  },

  height,

  props,

  emits: [
    'input'
  ],

  setup (_, ctx) {
    const { componentListeners } = useVue3Migration(ctx)

    return {
      componentListeners
    }
  },

  data () {
    return {
      focused: false
    }
  },

  computed: {
    classes () {
      return [
        'c-search-box', {
          '-focused': this.focused || !!this.value,
          '-alternative-focus': this.alternativeFocus,
          '-disabled': this.disabled
        }
      ]
    },

    // We need this to avoid emiting the 'input' event twice
    computedListeners () {
      const { input, ...remaining } = this.componentListeners

      return remaining
    },

    computedAttrs () {
      const { onInput, ...remaining } = this.$attrs

      return { ...remaining }
    }
  },

  methods: {
    resetValue () {
      this.$emit('input', '')
      this.focus()
    },

    onBlur () {
      this.focused = false
    },

    onEsc () {
      if (!this.noEscListener) {
        this.onBlur()

        this.resetValue()
      }
    },

    focus () {
      const { input } = this.$refs || {}
      if (input.focus) input.focus()
    },

    blur () {
      const { input } = this.$refs || {}
      if (input.blur) input.blur()
    },

    getHeight () {
      return height
    }
  }
}
</script>

<style lang="scss">
%typing {
  padding-left: 40px;
  background: #fff;
  box-shadow: 2px 8px 16px 0 rgba(24, 50, 115, 0.06);
}

.c-search-box {
  display: flex;
  min-height: var(--height);
  position: relative;

  & > .icon {
    top: 50%;
    right: 10px;
    position: absolute;
    box-sizing: content-box;

    transition: right .3s;
    transform: translate(0, -50%);

    @include icon-color(text, base-30);

    @include convenia-theme() {
      @include icon-color(var(--color-primary-30));
    }
  }

  & > .cancel {
    top: 50%;
    right: 10px;
    opacity: 0;
    position: absolute;
    transition: opacity 0.3s;
    transform: translateY(-50%);

    &.-theme-convenia-green { display: none; }

    @include convenia-theme() {
      &.-default { display: none; }

      &.-theme-convenia-green {
        display: flex;

        & > .icon {
          stroke: var(--color-primary-10);

          & line { stroke: var(--color-primary-50); }
        }
      }
    }
  }

  & > .input {
    flex: 1;
    width: 100%;
    outline: 0;
    border: none;
    height: var(--height);
    font-size: 14px;
    padding-left: 15px;
    border-radius: 20px;
    padding-right: 40px;
    transition: all .3s;
    box-shadow: 0 0 0 0 rgba(0,0,0,0);
    color: color-var(text, 80);
    background: color-var(primary, 5);

    @include convenia-theme() {
      color: var(--text-80);
    }

    &:focus { @extend %typing }
    &::placeholder {
      color: color-var(text, base-30);

      @include convenia-theme() {
        color: var(--text-30);
      }
    }
  }

  &.-alternative-focus > .input {
    box-shadow: unset;
    background: color-var(primary, 5);
  }

  &.-disabled > .input {
    cursor: default;

    &::placeholder {
      color: color-var(text, 30);
    }
  }

  &.-focused > .icon { right: calc(100% - 30px); }
  &.-focused > .cancel { opacity: 1; }
  &.-focused > .input { @extend %typing; }
}
</style>
